import { useMemo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Zap, Shield, Clock, Users, TrendingUp, CheckCircle, Activity,
  Target, Rocket, Globe, Server, Lock, BarChart3, Sparkles, ArrowRight, Calendar,
  Package, ChevronDown, Star, MessageSquare, Eye, Boxes, BookOpen, Building2,
  ExternalLink, Briefcase, GraduationCap, Network, Wrench, Award
} from 'lucide-react'
import { Button } from '@/components/ui/button'

const About = () => {
  const navigate = useNavigate()
  const shouldReduceMotion = useReducedMotion()
  
  // Optimized animation variants with reduced motion support
  const fadeInUp = useMemo(() => ({
    initial: { opacity: 0, y: shouldReduceMotion ? 0 : 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: shouldReduceMotion ? 0 : 0.6 }
  }), [shouldReduceMotion])

  const fadeIn = useMemo(() => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: shouldReduceMotion ? 0 : 0.5 }
  }), [shouldReduceMotion])

  // Optimized viewport settings for intersection observer
  const viewportSettings = useMemo(() => ({ once: true, amount: 0.3, margin: "0px 0px -100px 0px" }), [])


  // Memoized target audiences data for performance
  const targetAudiences = useMemo(
    () => [
      {
        icon: Briefcase,
        title: 'Vendor Alliances',
        description: 'Co-create go-to-market programmes with HPE Aruba, Juniper, and ecosystem partners backed by real demo assets.',
        color: 'from-blue-500 to-blue-600',
        borderColor: 'border-blue-500/30',
        hoverBorder: 'group-hover:border-blue-500/60',
        iconBg: 'from-blue-500/20 to-blue-600/20',
        glowColor: 'blue-500'
      },
      {
        icon: Network,
        title: 'Channel Partners & Resellers',
        description: 'Bring customer pursuits to life through guided sessions, competitive positioning, and jointly built architectures.',
        color: 'from-purple-500 to-purple-600',
        borderColor: 'border-purple-500/30',
        hoverBorder: 'group-hover:border-purple-500/60',
        iconBg: 'from-purple-500/20 to-purple-600/20',
        glowColor: 'purple-500'
      },
      {
        icon: Wrench,
        title: 'Presales & Engineering Teams',
        description: 'Refine solution approaches, validate integrations, and align proof-of-concept criteria before customer workshops.',
        color: 'from-green-500 to-green-600',
        borderColor: 'border-green-500/30',
        hoverBorder: 'group-hover:border-green-500/60',
        iconBg: 'from-green-500/20 to-green-600/20',
        glowColor: 'green-500'
      },
      {
        icon: BarChart3,
        title: 'Commercial & Sales Leaders',
        description: 'Develop executive narratives, quantify value, and orchestrate campaign-driven follow-ups for partner growth.',
        color: 'from-orange-500 to-orange-600',
        borderColor: 'border-orange-500/30',
        hoverBorder: 'group-hover:border-orange-500/60',
        iconBg: 'from-orange-500/20 to-orange-600/20',
        glowColor: 'orange-500'
      },
      {
        icon: Boxes,
        title: 'Lifecycle & Services Teams',
        description: 'Align service design, adoption roadmaps, and managed offerings with the live demonstrations delivered in the lab.',
        color: 'from-pink-500 to-pink-600',
        borderColor: 'border-pink-500/30',
        hoverBorder: 'group-hover:border-pink-500/60',
        iconBg: 'from-pink-500/20 to-pink-600/20',
        glowColor: 'pink-500'
      },
      {
        icon: GraduationCap,
        title: 'Enablement & Training Leads',
        description: 'Leverage the consolidated knowledge base to plan partner academies, certifications, and follow-on education.',
        color: 'from-cyan-500 to-cyan-600',
        borderColor: 'border-cyan-500/30',
        hoverBorder: 'group-hover:border-cyan-500/60',
        iconBg: 'from-cyan-500/20 to-cyan-600/20',
        glowColor: 'cyan-500'
      }
    ],
    []
  )


  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
      style={{ 
        transform: 'translate3d(0,0,0)',
        backfaceVisibility: 'hidden',
        perspective: 1000,
      }}
    >
      {/* Hero Section - Problem Statement */}
      <section className="relative pt-20 pb-16 px-4 overflow-hidden">
        {/* Refined Professional Background */}
        <div className="absolute inset-0">
          {/* Subtle radial gradients */}
          <div className="absolute top-0 left-0 right-0 h-[600px] bg-gradient-to-b from-[#BBD134]/5 via-transparent to-transparent" />

          {/* Animated floating elements */}
          <motion.div
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-32 right-1/4 w-80 h-80 bg-gradient-to-br from-[#BBD134]/10 to-green-500/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              y: [0, 40, 0],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            className="absolute bottom-32 left-1/4 w-96 h-96 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"
          />
        </div>

        <div className="relative max-w-7xl mx-auto">

          {/* Main Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            {/* Sleek Status Indicator Bar */}
            <motion.a
              href="https://www.westcoast.co.uk/partner.html"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02, transition: { duration: 0.15 } }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border border-[#BBD134]/30 shadow-2xl mb-10 hover:border-[#BBD134]/50 hover:shadow-[#BBD134]/20 cursor-pointer group"
              style={{ willChange: 'transform' }}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="relative"
              >
                <div className="w-3 h-3 rounded-full bg-[#BBD134] shadow-lg shadow-[#BBD134]/50" />
                <div className="absolute inset-0 w-3 h-3 rounded-full bg-[#BBD134] animate-ping" />
              </motion.div>
              <div className="h-8 w-[1px] bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-600 to-transparent" />
              <span className="text-sm font-bold text-gray-800 dark:text-gray-100 tracking-wide">
                WESTCOAST PARTNER PROGRAMME
              </span>
              <div className="h-8 w-[1px] bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-600 to-transparent" />
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Live Lab Access
              </span>
              <ExternalLink className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </motion.a>

            {/* Bold Professional Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-[1.1]"
            >
              <span className="block text-gray-900 dark:text-white mb-2">
                Inside Westcoast&apos;s
              </span>
              <span className="block relative">
                <span className="bg-gradient-to-r from-[#BBD134] via-green-500 to-emerald-600 bg-clip-text text-transparent">
                  Technology Solutions Lab
                </span>
              </span>
            </motion.h1>

            {/* Clean Feature Tags */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-wrap items-center justify-center gap-3 mb-8"
            >
              {[
                { icon: Shield, text: 'Channel Excellence' },
                { icon: Network, text: 'Mission-Critical' },
                { icon: Award, text: 'Partner-Focused' }
              ].map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ borderColor: 'rgba(187, 209, 52, 0.5)' }}
                  transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 border border-gray-200 dark:border-gray-600"
                  style={{ willChange: 'border-color' }}
                >
                  <item.icon className="h-4 w-4 text-[#BBD134]" strokeWidth={2.5} />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {item.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* Refined Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="text-lg md:text-xl text-gray-700 dark:text-gray-200 max-w-4xl mx-auto leading-relaxed font-medium"
            >
              Discover the governance, people, and methodology that sit behind every partner demonstration we deliver across Europe.
            </motion.p>

            {/* Decorative glowing line separator */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="mt-12 mx-auto w-32 h-[2px] bg-gradient-to-r from-transparent via-[#BBD134] to-transparent relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#BBD134] to-transparent blur-sm" />
            </motion.div>
          </motion.div>

          {/* Welcome Message - Subtle Enhancement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl p-8 md:p-12 shadow-2xl border-2 border-blue-500/20 hover:border-blue-500/40 transition-colors duration-300 mb-16 overflow-hidden group"
          >
            {/* Subtle top accent line */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

            {/* Glass reflection */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-transparent pointer-events-none" />

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-200 leading-relaxed text-center">
              Meet the <span className="font-bold text-blue-600 dark:text-blue-400">Westcoast specialists</span> who curate live product demonstrations, guide solution conversations, and translate technical capabilities into practical recommendations. The <span className="font-black text-blue-600 dark:text-blue-400">Technology Solutions Lab</span>{' '}
              is a partner-exclusive programme—our internal facility where Aruba and Juniper solutions are showcased through guided, production-grade scenarios backed by consolidated resources.
            </p>
            <div className="mt-8 flex justify-center">
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="border-2 border-blue-500/40 text-blue-600 dark:text-blue-400 hover:border-blue-500 hover:bg-blue-500/5 font-semibold transition-all duration-200"
                >
                  <ArrowRight className="mr-2 h-5 w-5 rotate-180" />
                  Return to Experience Overview
                </Button>
              </motion.div>
            </div>
          </motion.div>

          {/* About Westcoast Technology Solutions - Premium Company Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative mb-32"
          >
            {/* Elegant background with subtle gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 rounded-3xl -z-10" />

            <div className="relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-8 md:p-16 shadow-2xl border-2 border-[#BBD134]/30 overflow-hidden">
              {/* Decorative corner accents */}
              <div className="absolute top-0 left-0 w-32 h-32 border-l-4 border-t-4 border-[#BBD134] rounded-tl-3xl" />
              <div className="absolute bottom-0 right-0 w-32 h-32 border-r-4 border-b-4 border-[#BBD134] rounded-br-3xl" />

              {/* Glass reflection overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />

              {/* Content */}
              <div className="relative">
                {/* Company Badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring' }}
                  className="flex justify-center mb-8"
                >
                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-[#BBD134] to-green-600 shadow-xl">
                    <Building2 className="h-6 w-6 text-white" strokeWidth={2} />
                    <span className="text-white font-bold text-lg">Westcoast Technology Solutions</span>
                  </div>
                </motion.div>

                {/* Mission Statement - Large and Bold */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="text-center mb-12"
                >
                  <h2 className="text-2xl md:text-4xl font-black mb-6 text-gray-900 dark:text-white leading-tight">
                    One Programme, One Partner Community
                  </h2>
                  <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-200 leading-relaxed max-w-5xl mx-auto font-medium">
                    Our multidisciplinary Technology Solutions experts underpin Westcoast&apos;s position as a leading European distributor. Following our integration into a wider corporate group, the lab ensures continuity across alliances, geographies, and delivery standards. We create presales engagements that convert <span className="font-bold text-[#BBD134]">complex digital transformation challenges</span> into clear, channel-ready conversations—aggregating collateral, orchestrating demos, and aligning the right people for every pursuit.
                  </p>
                </motion.div>

                {/* Mission Box */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="relative mb-12"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#BBD134]/10 via-blue-500/10 to-purple-500/10 rounded-2xl blur-xl" />
                  <div className="relative bg-gradient-to-br from-[#BBD134]/5 to-green-600/5 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-2 border-[#BBD134]/20">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#BBD134] to-green-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                        <Target className="h-6 w-6 text-white" strokeWidth={2} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-black mb-3 text-gray-900 dark:text-white">OUR MISSION</h3>
                        <p className="text-lg text-gray-700 dark:text-gray-200 leading-relaxed">
                          At Westcoast Technology Solutions we engineer engagements that merge <strong>real infrastructure, guided expertise, and clear storytelling</strong>. Each lab session is reserved for vendors and channel partners, designed to demonstrate value, reduce rework, and anchor opportunity progression on shared artefacts.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Value Proposition Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="grid md:grid-cols-2 gap-6 mb-12"
                >
                  {[
                    {
                      icon: Rocket,
                      title: 'Accelerate Partner Success',
                      description: 'Leverage curated demo assets, shared playbooks, and technical expertise to drive faster opportunity progression for the channel.'
                    },
                    {
                      icon: Users,
                      title: 'Expert Collaboration',
                      description: 'Access certified presales specialists who co-host workshops, lead demonstrations, and translate technology strategy into customer-ready outcomes.'
                    }
                  ].map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                      className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#BBD134]/20 to-green-600/20 flex items-center justify-center flex-shrink-0">
                          <item.icon className="h-6 w-6 text-[#BBD134]" strokeWidth={2} />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">{item.title}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* CTA to Main Westcoast Site */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="text-center"
                >
                  <a
                    href="https://www.westcoast.co.uk/TechnologySolutions.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#BBD134] to-green-600 text-white font-bold text-lg rounded-xl shadow-2xl hover:shadow-[#BBD134]/50 transition-shadow duration-300 group"
                  >
                    <Globe className="h-5 w-5 group-hover:rotate-12 transition-transform duration-300" />
                    Visit Westcoast Technology Solutions
                    <ExternalLink className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                  </a>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                    Learn more about our complete range of services
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* The Challenge Section - CEO Premium Edition */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 relative"
          >
            {/* Subtle background gradient orbs */}
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-[#BBD134]/10 via-blue-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gradient-to-br from-purple-500/10 via-[#BBD134]/5 to-transparent rounded-full blur-3xl pointer-events-none" />

            <div className="relative text-center mb-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="inline-flex items-center justify-center mb-8"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#BBD134]/20 via-blue-500/10 to-purple-500/20 rounded-full blur-2xl" />
                  <div className="relative w-20 h-20 bg-gradient-to-br from-[#BBD134] to-green-600 rounded-2xl flex items-center justify-center shadow-2xl">
                    <Target className="h-10 w-10 text-white" strokeWidth={2} />
                  </div>
                </div>
              </motion.div>

              <h2 className="text-4xl md:text-6xl font-black mb-6 text-gray-900 dark:text-white">
                Solving Critical <span className="bg-gradient-to-r from-[#BBD134] via-green-500 to-blue-600 bg-clip-text text-transparent">Challenges</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto font-medium">
                Before you even reach the booking form, we help teams remove the friction points that slow down confident technology decisions.
              </p>
            </div>

            <div className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: MessageSquare,
                  title: 'Information Overload',
                  description: 'Technical specifications scattered across multiple vendor sites, making comparative analysis time-consuming and inefficient.',
                  accentColor: 'from-[#BBD134] to-green-500',
                  borderColor: 'border-[#BBD134]/20',
                  hoverBorder: 'group-hover:border-[#BBD134]/40',
                  glowColor: '[#BBD134]'
                },
                {
                  icon: Eye,
                  title: 'Lack of Hands-On Experience',
                  description: 'Making million-dollar infrastructure decisions without the ability to physically test and validate the technology.',
                  accentColor: 'from-green-500 to-emerald-500',
                  borderColor: 'border-green-500/20',
                  hoverBorder: 'group-hover:border-green-500/40',
                  glowColor: 'green-500'
                },
                {
                  icon: Clock,
                  title: 'Complex Scheduling',
                  description: 'Endless email chains and calendar conflicts when coordinating demonstration sessions across multiple stakeholders.',
                  accentColor: 'from-emerald-500 to-teal-500',
                  borderColor: 'border-emerald-500/20',
                  hoverBorder: 'group-hover:border-emerald-500/40',
                  glowColor: 'emerald-500'
                },
                {
                  icon: BookOpen,
                  title: 'Missed Learning Opportunities',
                  description: 'No centralized hub for workshops, webinars, and product launches, leading to fragmented knowledge.',
                  accentColor: 'from-teal-500 to-cyan-500',
                  borderColor: 'border-teal-500/20',
                  hoverBorder: 'group-hover:border-teal-500/40',
                  glowColor: 'teal-500'
                },
                {
                  icon: Boxes,
                  title: 'Uncertainty in Decision Making',
                  description: 'Unable to validate networking solutions against specific use cases and organizational requirements.',
                  accentColor: 'from-[#BBD134] to-lime-500',
                  borderColor: 'border-lime-500/20',
                  hoverBorder: 'group-hover:border-lime-500/40',
                  glowColor: 'lime-500'
                },
                {
                  icon: Target,
                  title: 'Risk & Validation',
                  description: 'Difficulty testing real-world scenarios and performance metrics before committing to expensive deployments.',
                  accentColor: 'from-green-600 to-emerald-700',
                  borderColor: 'border-green-600/20',
                  hoverBorder: 'group-hover:border-green-600/40',
                  glowColor: 'green-600'
                },
              ].map((challenge, index) => (
                <motion.div
                  key={challenge.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                  className="group relative h-full"
                >
                  {/* Glass card with backdrop blur and shine */}
                  <motion.div
                    whileHover={{ y: -8, scale: 1.02 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className={`relative h-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-xl border-2 ${challenge.borderColor} ${challenge.hoverBorder} overflow-hidden`}
                    style={{
                      willChange: 'transform',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden'
                    }}
                  >
                    {/* Strong glass reflection effect - top to bottom */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent rounded-2xl pointer-events-none" />

                    {/* Diagonal shine stripe - creates glass-like reflection */}
                    <div className="absolute -top-1/2 -left-1/4 w-1/2 h-full bg-gradient-to-br from-white/30 via-white/10 to-transparent transform rotate-12 pointer-events-none" />

                    {/* Edge highlight - left side */}
                    <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/40 to-transparent pointer-events-none" />

                    {/* Top edge highlight */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-t-2xl pointer-events-none" />

                    {/* Animated shimmer effect overlay */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                        animate={{
                          x: ['-200%', '200%']
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'linear',
                          repeatDelay: 1
                        }}
                        style={{ transformOrigin: 'left' }}
                      />
                    </motion.div>

                    {/* Top accent bar with glass effect */}
                    <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${challenge.accentColor} rounded-t-2xl`} />
                    <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${challenge.accentColor} rounded-t-2xl opacity-50 blur-sm`} />

                    {/* Icon container with enhanced glass effect */}
                    <div className="relative mb-6">
                      <motion.div
                        initial={{ scale: 1 }}
                        whileHover={{ scale: 1.15, rotate: 5 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="relative"
                      >
                        {/* Glass icon background with strong shine */}
                        <div className={`relative w-16 h-16 rounded-xl bg-gradient-to-br ${challenge.accentColor} flex items-center justify-center shadow-2xl overflow-hidden`}>
                          {/* Strong glass reflection - top left */}
                          <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/20 to-transparent rounded-xl" />

                          {/* Diagonal shine stripe */}
                          <div className="absolute -top-2 -left-2 w-12 h-12 bg-gradient-to-br from-white/50 to-transparent transform rotate-45 rounded-xl" />

                          {/* Bottom right subtle reflection */}
                          <div className="absolute inset-0 bg-gradient-to-tl from-white/20 via-transparent to-transparent rounded-xl" />

                          {/* Edge highlights */}
                          <div className="absolute top-0 left-0 right-0 h-px bg-white/60 rounded-t-xl" />
                          <div className="absolute top-0 bottom-0 left-0 w-px bg-white/60 rounded-l-xl" />

                          <challenge.icon className="relative h-8 w-8 text-white drop-shadow-lg" strokeWidth={2} />
                        </div>
                      </motion.div>
                    </div>

                    {/* Title with slight gradient on hover */}
                    <h3 className="font-bold text-xl mb-3 text-gray-900 dark:text-white transition-colors duration-300 group-hover:text-gray-700 dark:group-hover:text-gray-100">
                      {challenge.title}
                    </h3>

                    {/* Description with consistent height */}
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed min-h-[4rem]">
                      {challenge.description}
                    </p>

                    {/* Bottom gradient line on hover - Safari-safe */}
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${challenge.accentColor} rounded-b-2xl`}
                      style={{ transformOrigin: 'left' }}
                    />

                    {/* Subtle corner accents - Safari-safe */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="absolute top-4 right-4 w-10 h-10 border-r-2 border-t-2 border-gray-300/50 dark:border-gray-600/50 rounded-tr-lg"
                    />
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Compact Transition Arrow */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center items-center my-8"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-2"
            >
              <ArrowRight className="h-8 w-8 text-[#BBD134] rotate-90" strokeWidth={3} />
              <span className="text-xs font-bold text-[#BBD134] uppercase tracking-wider">Solution</span>
            </motion.div>
          </motion.div>

          {/* Our Solution - Green Glass */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative rounded-3xl p-12 md:p-16 text-center shadow-2xl mb-0 overflow-hidden"
          >
            {/* Green glass background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#BBD134]/80 via-green-500/70 to-emerald-600/80 backdrop-blur-xl" />

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(187,209,52,0.35)_0%,transparent_55%)]" />

            {/* Glass reflection */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />

            <div className="relative text-white">
              <Sparkles className="h-16 w-16 mx-auto mb-6" />
              <h3 className="text-3xl md:text-5xl font-black mb-6">Our Solution</h3>
              <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto font-medium">
                A unified platform combining <strong>live product demonstrations</strong>, <strong>scheduled events and webinars</strong>, <strong>technical workshops</strong>, and <strong>curated resources</strong>—giving partners everything needed to evaluate solutions with confidence. Browse the catalog, arrange product demonstrations, or join our event calendar for training and product launches.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <motion.div
                  whileHover={{ y: -6, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Button
                    size="lg"
                    onClick={() => navigate('/demos')}
                    className="bg-white text-[#BBD134] hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-2xl transition-colors duration-300 font-bold"
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Explore Demos
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ y: -6, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Button
                    size="lg"
                    onClick={() => navigate('/events')}
                    className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30 px-8 py-6 text-lg rounded-xl shadow-2xl transition-colors duration-300 font-bold"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    View Events
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* How It Works Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-32 mb-20"
          >
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-black mb-4 text-gray-900 dark:text-white">
                How It <span className="bg-gradient-to-r from-[#BBD134] to-green-600 bg-clip-text text-transparent">Works</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Get started in four simple steps
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 max-w-7xl mx-auto">
              {[
                {
                  number: '01',
                  icon: Globe,
                  title: 'Browse the Catalog',
                  description: 'Explore demo sessions and upcoming events including webinars, workshops, and product launches to find what matches your customer needs.'
                },
                {
                  number: '02',
                  icon: Calendar,
                  title: 'Book Your Session',
                  description: 'Select demos for customer engagements or join scheduled events from the calendar. Complete the booking form with session details.'
                },
                {
                  number: '03',
                  icon: Activity,
                  title: 'We Coordinate & Deliver',
                  description: 'Our Solutions Lab team receives your request, coordinates the session, and delivers live guided demonstrations with hands-on access.'
                },
                {
                  number: '04',
                  icon: CheckCircle,
                  title: 'Progress with Confidence',
                  description: 'Gain competitive positioning insights, product knowledge, solution guidance, and access to curated resources that support your evaluation and next steps.'
                }
              ].map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative group"
                >
                  <div className="text-center relative">
                    {/* Number badge */}
                    <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#BBD134] to-green-600 text-white font-black text-xl mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300 z-10">
                      {step.number}
                    </div>
                    {/* Connector line between steps - positioned behind circles */}
                    {index < 3 && (
                      <div className="hidden md:block absolute left-1/2 top-8 h-0.5 bg-gradient-to-r from-transparent via-[#BBD134] to-transparent -z-10" style={{ width: 'calc(100% + 2rem)' }} />
                    )}

                    {/* Icon */}
                    <div className="flex justify-center mb-4">
                      <div className="p-4 rounded-xl bg-white dark:bg-gray-800 shadow-md group-hover:shadow-xl transition-shadow duration-300">
                        <step.icon className="h-8 w-8 text-[#BBD134]" strokeWidth={2} />
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="font-bold text-xl mb-2 text-gray-900 dark:text-white">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>
      </section>


      {/* Core Platform Features - Detailed Showcase */}
      <section className="relative py-32 px-4 overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-blue-950 dark:via-purple-950 dark:to-pink-950">
        {/* Background gradient layers for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 via-purple-100/50 to-pink-100/50 dark:from-blue-950/50 dark:via-purple-950/50 dark:to-pink-950/50" />

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, type: 'spring' }}
              className="inline-flex mb-8"
            >
              <div className="p-5 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-xl">
                <Package className="h-12 w-12 text-white" strokeWidth={2} />
              </div>
            </motion.div>

            <h2 className="text-4xl md:text-6xl font-black mb-6 text-gray-900 dark:text-white">
              Core Platform <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              Everything you need to evaluate, experience, and implement cutting-edge networking solutions
            </p>
          </motion.div>

          {/* Feature Categories */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Demo Catalog */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border-2 border-blue-500/30 hover:border-blue-500/50 overflow-hidden relative group"
              style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden'
              }}
            >
              {/* Glass effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent rounded-2xl pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-t-2xl pointer-events-none" />

              {/* Animated shimmer effect overlay */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  animate={{
                    x: ['-200%', '200%']
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatDelay: 1
                  }}
                  style={{ transformOrigin: 'left' }}
                />
              </motion.div>

              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg overflow-hidden relative"
                  >
                    {/* Glass reflection on icon */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/20 to-transparent rounded-xl" />
                    <div className="absolute -top-2 -left-2 w-10 h-10 bg-gradient-to-br from-white/50 to-transparent transform rotate-45 rounded-xl" />
                    <Server className="relative h-7 w-7 text-white drop-shadow-lg" strokeWidth={2} />
                  </motion.div>
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white">Demo Catalog</h3>
                </div>

                <p className="text-gray-700 dark:text-gray-200 mb-6 leading-relaxed">
                  Hands-on access to live demonstrations featuring the latest networking technologies:
                </p>

                <ul className="space-y-3">
                  {[
                    'HPE Aruba Central - Cloud-native network management',
                    'Juniper Mist AI - AI-driven wireless & wired assurance',
                    'Wi-Fi 6E/7 - Next-generation wireless performance',
                    'Zero Trust Security - Advanced threat protection',
                    'SD-WAN Solutions - Intelligent WAN optimization',
                    'Network Analytics - Real-time insights & reporting'
                  ].map((item, index) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Event Categories */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border-2 border-purple-500/30 hover:border-purple-500/50 overflow-hidden relative group"
              style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden'
              }}
            >
              {/* Glass effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent rounded-2xl pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-t-2xl pointer-events-none" />

              {/* Animated shimmer effect overlay */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  animate={{
                    x: ['-200%', '200%']
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatDelay: 1
                  }}
                  style={{ transformOrigin: 'left' }}
                />
              </motion.div>

              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg overflow-hidden relative"
                  >
                    {/* Glass reflection on icon */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/20 to-transparent rounded-xl" />
                    <div className="absolute -top-2 -left-2 w-10 h-10 bg-gradient-to-br from-white/50 to-transparent transform rotate-45 rounded-xl" />
                    <Calendar className="relative h-7 w-7 text-white drop-shadow-lg" strokeWidth={2} />
                  </motion.div>
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white">Events Platform</h3>
                </div>

                <p className="text-gray-700 dark:text-gray-200 mb-6 leading-relaxed">
                  Comprehensive learning and networking opportunities tailored for IT professionals:
                </p>

                <ul className="space-y-3">
                  {[
                    'Technical Workshops - Deep-dive training sessions',
                    'Product Webinars - Latest feature announcements',
                    'Launch Events - New technology introductions',
                    'Certification Training - Professional development',
                    'Customer Success Events - Best practices sharing',
                    'Partner Meetups - Network with industry experts'
                  ].map((item, index) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <Star className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Booking System */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border-2 border-green-500/30 hover:border-green-500/50 overflow-hidden relative group"
              style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden'
              }}
            >
              {/* Glass effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent rounded-2xl pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-t-2xl pointer-events-none" />

              {/* Animated shimmer effect overlay */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  animate={{
                    x: ['-200%', '200%']
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatDelay: 1
                  }}
                  style={{ transformOrigin: 'left' }}
                />
              </motion.div>

              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg overflow-hidden relative"
                  >
                    {/* Glass reflection on icon */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/20 to-transparent rounded-xl" />
                    <div className="absolute -top-2 -left-2 w-10 h-10 bg-gradient-to-br from-white/50 to-transparent transform rotate-45 rounded-xl" />
                    <Clock className="relative h-7 w-7 text-white drop-shadow-lg" strokeWidth={2} />
                  </motion.div>
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white">Smart Booking</h3>
                </div>

                <p className="text-gray-700 dark:text-gray-200 mb-6 leading-relaxed">
                  Intelligent scheduling system that simplifies the entire booking process:
                </p>

                <ul className="space-y-3">
                  {[
                    'Real-time availability checking',
                    'Flexible time slot selection',
                    'Automated email confirmations',
                    'Calendar integration (iCal, Google)',
                    'Easy rescheduling & cancellation',
                    'Admin approval workflows'
                  ].map((item, index) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Lab Status Dashboard */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border-2 border-pink-500/30 hover:border-pink-500/50 overflow-hidden relative group"
              style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden'
              }}
            >
              {/* Glass effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent rounded-2xl pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-t-2xl pointer-events-none" />

              {/* Animated shimmer effect overlay */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  animate={{
                    x: ['-200%', '200%']
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatDelay: 1
                  }}
                  style={{ transformOrigin: 'left' }}
                />
              </motion.div>

              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center shadow-lg overflow-hidden relative"
                  >
                    {/* Glass reflection on icon */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/20 to-transparent rounded-xl" />
                    <div className="absolute -top-2 -left-2 w-10 h-10 bg-gradient-to-br from-white/50 to-transparent transform rotate-45 rounded-xl" />
                    <Activity className="relative h-7 w-7 text-white drop-shadow-lg" strokeWidth={2} />
                  </motion.div>
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white">Live Monitoring</h3>
                </div>

                <p className="text-gray-700 dark:text-gray-200 mb-6 leading-relaxed">
                  Real-time visibility into lab infrastructure health and performance:
                </p>

                <ul className="space-y-3">
                  {[
                    'Device status & availability',
                    'Network performance metrics',
                    'Uptime tracking (99.9% SLA)',
                    'Historical data & trends',
                    'Automated health checks',
                    'Instant alert notifications'
                  ].map((item, index) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <Activity className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ideal For Organizations - Target Audience Showcase */}
      <section className="relative py-32 px-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, type: 'spring' }}
              className="inline-flex mb-8"
            >
              <div className="p-5 rounded-2xl bg-gradient-to-br from-[#BBD134] to-green-600 shadow-xl">
                <Users className="h-12 w-12 text-white" strokeWidth={2} />
              </div>
            </motion.div>

            <h2 className="text-4xl md:text-6xl font-black mb-6 text-gray-900 dark:text-white">
              Ideal For <span className="bg-gradient-to-r from-[#BBD134] to-green-600 bg-clip-text text-transparent">Organizations</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Empowering IT professionals across all disciplines to make confident, informed technology decisions
            </p>
          </motion.div>

          {/* Target Audience Grid - Premium Enhanced Version */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {targetAudiences.map((audience, index) => (
              <motion.div
                key={audience.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group h-full"
              >
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className={`h-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border-2 ${audience.borderColor} ${audience.hoverBorder} overflow-hidden relative`}
                  style={{
                    willChange: 'transform',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden'
                  }}
                >
                  {/* Strong glass reflection effect - top to bottom */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent rounded-2xl pointer-events-none" />

                  {/* Diagonal shine stripe - creates glass-like reflection */}
                  <div className="absolute -top-1/2 -left-1/4 w-1/2 h-full bg-gradient-to-br from-white/30 via-white/10 to-transparent transform rotate-12 pointer-events-none" />

                  {/* Edge highlight - left side */}
                  <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/40 to-transparent pointer-events-none" />

                  {/* Top edge highlight */}
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-t-2xl pointer-events-none" />

                  {/* Top accent bar with double layer for depth */}
                  <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${audience.color} rounded-t-2xl`} />
                  <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${audience.color} rounded-t-2xl opacity-50 blur-sm`} />

                  {/* Content */}
                  <div className="relative">
                    {/* Icon with enhanced glass effects */}
                    <motion.div
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.15, rotate: 5 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="mb-6 relative"
                    >
                      {/* Glass icon background with strong shine */}
                      <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${audience.color} flex items-center justify-center shadow-2xl overflow-hidden relative`}>
                        {/* Strong glass reflection - top left */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/20 to-transparent rounded-xl" />

                        {/* Diagonal shine stripe */}
                        <div className="absolute -top-2 -left-2 w-14 h-14 bg-gradient-to-br from-white/50 to-transparent transform rotate-45 rounded-xl" />

                        {/* Bottom right subtle reflection */}
                        <div className="absolute inset-0 bg-gradient-to-tl from-white/20 via-transparent to-transparent rounded-xl" />

                        {/* Edge highlights */}
                        <div className="absolute top-0 left-0 right-0 h-px bg-white/60 rounded-t-xl" />
                        <div className="absolute top-0 bottom-0 left-0 w-px bg-white/60 rounded-l-xl" />

                        <audience.icon className="relative h-10 w-10 text-white drop-shadow-lg" strokeWidth={2} />
                      </div>
                    </motion.div>

                    {/* Title */}
                    <h3 className="text-xl font-black mb-3 text-gray-900 dark:text-white leading-tight">
                      {audience.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed min-h-[4rem]">
                      {audience.description}
                    </p>

                    {/* Bottom gradient accent on hover */}
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className={`absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r ${audience.color} rounded-b-2xl`}
                      style={{ transformOrigin: 'left' }}
                    />
                  </div>

                  {/* Glass reflection effect on bottom */}
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white/5 to-transparent rounded-b-2xl pointer-events-none" />
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Call-out Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16 text-center"
          >
            <div className="inline-block bg-gradient-to-r from-[#BBD134]/10 via-green-500/10 to-emerald-500/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#BBD134]/30">
              <p className="text-lg md:text-xl text-gray-700 dark:text-gray-200 max-w-3xl">
                <strong className="text-[#BBD134]">Whether you're a network engineer, IT decision-maker, or technical consultant</strong>,
                our platform provides the tools and resources you need to confidently evaluate, implement, and optimize
                enterprise networking solutions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#BBD134] via-blue-600 to-purple-600" />

        <div className="relative max-w-5xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Ready to Put the Lab to Work?
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-white/90">
              Take what you have seen here and step into the booking journey—choose the experience on the Home page that matches your goal, or jump straight to a guided session.
            </p>

            <div className="flex flex-wrap gap-6 justify-center">
              <motion.div
                whileHover={{ y: -6, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <Button
                  size="lg"
                  onClick={() => navigate('/demos')}
                  className="bg-gradient-to-r from-[#BBD134] to-green-500 text-gray-900 hover:from-[#BBD134]/90 hover:to-green-500/90 px-10 py-7 text-lg rounded-xl shadow-2xl hover:shadow-[#BBD134]/50 transition-colors duration-300 font-bold"
                >
                  <Rocket className="mr-2 h-6 w-6" />
                  Book a Session
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ y: -6, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <Button
                  size="lg"
                  onClick={() => navigate('/')}
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-10 py-7 text-lg rounded-xl shadow-2xl transition-colors duration-300 font-bold"
                >
                  <Activity className="mr-2 h-6 w-6" />
                  Visit Home Overview
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <section className="relative py-16 px-4 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xl font-bold text-white mb-3 bg-gradient-to-r from-[#BBD134] via-green-400 to-[#BBD134] bg-clip-text text-transparent">
            Westcoast Technology Solutions
          </p>
          <p className="text-base text-gray-300 font-semibold mb-2">
            Empowering Informed Technology Decisions
          </p>
          <p className="text-sm text-gray-400">
            Where enterprise networking innovation meets practical experience
          </p>
        </div>
      </section>
    </div>
  )
}

export default About
